/**
 * Concern:
 * Shared card import request/response schemas used across API, worker, and website packages.
 * Keeps the async import lifecycle contract anchored to the core package instead of local runtime definitions.
 */
import { z } from "zod";

import { UserImportSourceType } from "../types/enums.js";
import {
  UserImportCardStatusSchema,
  UserImportSourceTypeSchema,
  UserImportStateSchema,
} from "./domain.js";

export const CardImportSourceTypeSchema = UserImportSourceTypeSchema;

export const CardImportCreateUploadInputSchema = z.object({
  fileName: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
  contentType: z.string().min(1).optional(),
});

export const CardImportCreateUploadResultSchema = z.object({
  importState: UserImportStateSchema,
  uploadUrl: z.string().url(),
  uploadMethod: z.literal("PUT"),
  uploadHeaders: z.record(z.string()),
  expiresAt: z.string(),
});

export const CardImportCompleteUploadInputSchema = z.object({
  importId: z.string().min(1),
});

export const CardImportCancelInputSchema = z.object({
  importId: z.string().min(1),
  reason: z.string().min(1).optional(),
});

export const CardImportDeleteHistoryInputSchema = z.object({
  importId: z.string().min(1),
});

export const CardImportDeleteHistoryResultSchema = z.object({
  importId: z.string().min(1),
});

export const CardImportQueueMessageSchema = z.discriminatedUnion("sourceType", [
  z.object({
    importId: z.string().min(1),
    userId: z.string().min(1),
    sourceType: z.literal(UserImportSourceType.UPLOAD),
    storageBucket: z.string().min(1),
    storageKey: z.string().min(1),
  }),
  // Legacy-only queue shape kept so old messages fail cleanly after URL import removal.
  z.object({
    importId: z.string().min(1),
    userId: z.string().min(1),
    sourceType: z.literal(UserImportSourceType.URL),
    sourceUrl: z.string().url(),
  }),
]);

export const CardImportDetailInputSchema = z.object({
  importId: z.string().min(1),
});

export const CardImportCardStatusesInputSchema = z.object({
  importId: z.string().min(1),
  cursor: z.string().min(1).optional(),
  pageSize: z.number().int().positive().max(100).optional(),
});

export const CardImportHistoryListSchema = z.object({
  currentState: UserImportStateSchema.optional(),
  activeImport: UserImportStateSchema.optional(),
  history: z.array(UserImportStateSchema),
});

export const CardImportDetailSchema = z.object({
  currentState: UserImportStateSchema.optional(),
  importState: UserImportStateSchema,
});

export const CardImportCardStatusesPageSchema = z.object({
  items: z.array(UserImportCardStatusSchema),
  nextCursor: z.string().optional(),
});
